import { gql, type TypedDocumentNode } from "@apollo/client";
import type { BackofficeCRMNestedTaskInput } from "@/hooks/useAttachTask";

export type ActiveCallPhoneSearchPerson = {
  id: string;
  pgaId: string | null;
  displayName: string;
  informalName: string | null;
  profilePhoto: string | null;
  email: string | null;
  primarySection: {
    name: string;
  } | null;
  primaryFacility: {
    id: string;
    name: string;
  } | null;
  phoneNumbers: {
    phoneNumber: string;
    phoneFormatted: string;
  }[];
};

export type ActiveCallPhoneSearchResponse = {
  people: {
    nodes: ActiveCallPhoneSearchPerson[];
  };
};

export type ActiveCallPhoneSearchVariables = {
  input: {
    phone: string;
  };
  first: number;
};

export const ACTIVE_CALL_PHONE_SEARCH_QUERY: TypedDocumentNode<
  ActiveCallPhoneSearchResponse,
  ActiveCallPhoneSearchVariables
> = gql`
  query ActiveCallPhoneSearch($input: BackofficeCRMPeopleInput!, $first: Int) {
    people(input: $input, first: $first) {
      nodes {
        id
        pgaId
        displayName
        informalName
        profilePhoto
        email
        primarySection {
          name
        }
        primaryFacility {
          id
          name
        }
        phoneNumbers {
          phoneNumber
          phoneFormatted
        }
      }
    }
  }
`;

export type ActiveCallSelectedPerson = {
  id: string;
  pgaId: string;
  displayName: string;
  profilePhoto: string | null;
};

export type ActiveCallSelectedPersonResponse = {
  person: ActiveCallSelectedPerson | null;
};

export type ActiveCallSelectedPersonVariables = {
  pgaId: string;
};

export const ACTIVE_CALL_SELECTED_PERSON_QUERY: TypedDocumentNode<
  ActiveCallSelectedPersonResponse,
  ActiveCallSelectedPersonVariables
> = gql`
  query ActiveCallSelectedPerson($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      displayName
      profilePhoto
    }
  }
`;

export type TranscriptSummaryStatus = "pending" | "processing" | "completed" | "failed" | "unavailable";

export const TRANSCRIPT_SUMMARY_TERMINAL_STATUSES: TranscriptSummaryStatus[] = ["completed", "failed", "unavailable"];

export type ActiveCallTranscriptStatusCall = {
  callSid: string;
  endedAt: string | null;
  transcriptSummaryStatus: TranscriptSummaryStatus;
  transcriptSummary: string | null;
  interactionId: string | null;
};

export type ActiveCallTranscriptStatusResponse = {
  twilioFlexCall: ActiveCallTranscriptStatusCall | null;
};

export type ActiveCallTranscriptStatusVariables = {
  callSid: string;
};

export const ACTIVE_CALL_TRANSCRIPT_STATUS_QUERY: TypedDocumentNode<
  ActiveCallTranscriptStatusResponse,
  ActiveCallTranscriptStatusVariables
> = gql`
  query ActiveCallTranscriptStatus($callSid: String!) {
    twilioFlexCall(callSid: $callSid) {
      callSid
      endedAt
      transcriptSummaryStatus
      transcriptSummary
      interactionId
    }
  }
`;

export type ActiveCallFinalizeInteractionResponse = {
  createInteraction: {
    success: boolean;
    message: string | null;
    interaction: {
      id: string;
    } | null;
    task: {
      id: string;
    } | null;
  };
};

export type ActiveCallFinalizeInteractionVariables = {
  input: {
    participantId: string;
    interactionDate: string;
    mode: string;
    twilioFlexCallSid?: string | null;
    categories?: string[] | null;
    description: string | null;
    summary: string | null;
    privacyRestricted?: boolean;
    task?: BackofficeCRMNestedTaskInput | null;
  };
};

export const ACTIVE_CALL_FINALIZE_INTERACTION_MUTATION: TypedDocumentNode<
  ActiveCallFinalizeInteractionResponse,
  ActiveCallFinalizeInteractionVariables
> = gql`
  mutation ActiveCallFinalizeInteraction($input: BackofficeCRMCreateInteractionInput!) {
    createInteraction(input: $input) {
      success
      message
      interaction {
        id
      }
      task {
        id
      }
    }
  }
`;
